import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Select, Table } from 'antd';
import { listRooms, createRoom, updateRoom, deleteRoom, getRoomAssignments, bulkGenerateRooms } from '../../api/rooms';
import { listMyAssignedResidences } from '../../api/residences';
import { listResidenceBuildings } from '../../api/buildings';
import { getApiErrorMessage } from '../../utils/apiError';
import TableCard, { AntPagination } from '../../components/TableCard';
import { SelectWrap } from '../../components/ToolbarControls';
import { ViewIcon, EditIcon, DeleteIcon } from '../../components/UIKit';

export default function Rooms() {
  const { message, notification } = App.useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [residences, setResidences] = useState([]);
  const [residenceId, setResidenceId] = useState('');
  const [buildings, setBuildings] = useState([]);
  const [buildingsLoading, setBuildingsLoading] = useState(false);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewRoom, setViewRoom] = useState(null);
  const [viewAssignments, setViewAssignments] = useState([]);
  const [viewPage, setViewPage] = useState(1);
  const viewPageSize = 8;

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkForm] = Form.useForm();

  const selectedResidenceBulkId = Form.useWatch('residence_id', bulkForm);
  const selectedBuildingBulkId = Form.useWatch('building_id', bulkForm);
  const [bulkBuildingPickerOpen, setBulkBuildingPickerOpen] = useState(false);
  const [bulkBuildingSearch, setBulkBuildingSearch] = useState('');

  const selectedResidenceFormId = Form.useWatch('residence_id', form);
  const selectedBuildingFormId = Form.useWatch('building_id', form);
  const [buildingPickerOpen, setBuildingPickerOpen] = useState(false);
  const [buildingSearch, setBuildingSearch] = useState('');

  const fetchAll = useCallback(async (rid) => {
    setLoading(true);
    try {
      const params = {};
      if (rid) params.residence_id = rid;
      const data = await listRooms(params);
      setRows(data);
    } catch (e) {
      notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les chambres.'), placement: 'topRight' });
    } finally { setLoading(false); }
  }, [notification]);

  const fetchBuildings = useCallback(async (rid) => {
    const id = String(rid || '');
    if (!id) { setBuildings([]); return; }
    setBuildingsLoading(true);
    try { const data = await listResidenceBuildings(id); setBuildings(Array.isArray(data) ? data : []); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les bâtiments.'), placement: 'topRight' }); setBuildings([]); }
    finally { setBuildingsLoading(false); }
  }, [notification]);

  const fetchResidences = useCallback(async () => {
    try { const res = await listMyAssignedResidences(); setResidences(res); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les résidences.'), placement: 'topRight' }); setResidences([]); }
  }, [notification]);

  useEffect(() => { fetchResidences(); fetchAll(''); }, [fetchAll, fetchResidences]);
  useEffect(() => { fetchAll(residenceId); fetchBuildings(residenceId); }, [fetchAll, fetchBuildings, residenceId]);

  const filteredBuildings = useMemo(() => {
    const q = String(buildingSearch || '').trim().toLowerCase();
    if (!q) return buildings;
    return (buildings || []).filter((b) => String(b?.nom || '').toLowerCase().includes(q));
  }, [buildingSearch, buildings]);

  const filteredBulkBuildings = useMemo(() => {
    const q = String(bulkBuildingSearch || '').trim().toLowerCase();
    if (!q) return buildings;
    return (buildings || []).filter((b) => String(b?.nom || '').toLowerCase().includes(q));
  }, [bulkBuildingSearch, buildings]);

  const pagedViewAssignments = useMemo(() => {
    const base = Array.isArray(viewAssignments) ? viewAssignments : [];
    const start = (viewPage - 1) * viewPageSize;
    return base.slice(start, start + viewPageSize);
  }, [viewAssignments, viewPage]);

  const onAdd = () => { setEditing(null); form.resetFields(); form.setFieldsValue({ capacity: 1 }); setBuildingSearch(''); setOpen(true); };
  const onEdit = useCallback((record) => {
    setEditing(record);
    form.setFieldsValue({ residence_id: record.residence_id, building_id: record.building_id, code: record.code, capacity: record.capacity, notes: record.notes });
    setBuildingSearch(''); setOpen(true);
  }, [form]);

  const onView = useCallback(async (record) => {
    setViewRoom(record); setViewAssignments([]); setViewPage(1); setViewOpen(true); setViewLoading(true);
    try { const data = await getRoomAssignments(record.id); setViewAssignments(Array.isArray(data?.assignments) ? data.assignments : []); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les occupants.'), placement: 'topRight' }); setViewAssignments([]); }
    finally { setViewLoading(false); }
  }, [notification]);

  const onDelete = useCallback(async (record) => {
    try { await deleteRoom(record.id); message.success('Chambre supprimée.'); fetchAll(residenceId); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Suppression échouée.'), placement: 'topRight' }); }
  }, [fetchAll, message, notification, residenceId]);

  const onSubmit = async () => {
    try {
      const v = await form.validateFields();
      const payload = { residence_id: v.residence_id, building_id: v.building_id, code: String(v.code).trim().toUpperCase(), capacity: Number(v.capacity || 1), notes: v.notes || null };
      if (editing) { await updateRoom(editing.id, payload); message.success('Chambre modifiée.'); }
      else { await createRoom(payload); message.success('Chambre créée.'); }
      setOpen(false); fetchAll(residenceId);
    } catch (e) { if (e?.response || e?.message) notification.error({ message: getApiErrorMessage(e, 'Enregistrement échoué.'), placement: 'topRight' }); }
  };

  const onBulkOpen = () => {
    bulkForm.resetFields();
    bulkForm.setFieldsValue({ residence_id: residenceId || undefined, separator: '-', pad: 2, capacity: 1 });
    setBulkBuildingSearch(''); setBulkOpen(true);
  };

  const onBulkSubmit = async () => {
    setBulkSubmitting(true);
    try {
      const v = await bulkForm.validateFields();
      const b = (buildings || []).find((x) => String(x?.id) === String(v.building_id));
      const maxRooms = Number(b?.capacite_chambres || 0);
      if (!Number.isFinite(maxRooms) || maxRooms <= 0) {
        notification.error({ message: 'Veuillez définir la capacité (chambres) du bâtiment avant de générer.', placement: 'topRight' }); return;
      }
      const result = await bulkGenerateRooms({ residence_id: v.residence_id, building_id: v.building_id, start: 1, end: maxRooms, separator: v.separator, pad: Number(v.pad), capacity: Number(v.capacity || 1), notes: v.notes || null });
      const createdCount = Number(result?.createdCount || 0);
      const skippedCount = Number(result?.skippedExistingCount || 0);
      notification.success({ message: `Génération terminée: ${createdCount} créée(s), ${skippedCount} déjà existante(s)`, placement: 'topRight', duration: 8 });
      setBulkOpen(false); fetchAll(residenceId);
    } catch (e) { if (e?.response || e?.message) notification.error({ message: getApiErrorMessage(e, 'Génération échouée.'), placement: 'topRight' }); }
    finally { setBulkSubmitting(false); }
  };

  return (
    <div className="page">
      <TableCard
        label="Chambres"
        loading={loading}
        rows={rows}
        toolbar={<>
          <SelectWrap
            value={residenceId}
            onChange={setResidenceId}
            options={[{ value: '', label: 'Toutes les résidences' }, ...residences.map((r) => ({ value: r.id, label: r.nom }))]}
            style={{ minWidth: 200 }}
          />
          <button className="btn-outline" onClick={onBulkOpen}>Générer des chambres</button>
          <button className="btn-primary" onClick={onAdd}>+ Nouvelle chambre</button>
        </>}
      >
        {(pageRows) => (
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Capacité</th>
              <th>Occupants</th>
              <th>Résidence</th>
              <th>Bâtiment</th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.id}>
                <td>{r.code}</td>
                <td>{r.capacity ?? 1}</td>
                <td>{`${Number(r?.active_occupants || 0)}/${Number(r?.capacity || 0)}`}</td>
                <td>{r?.Residence?.nom || '-'}</td>
                <td>{r?.Building?.nom || '-'}</td>
                <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.notes || '-'}</td>
                <td>
                  <div className="actions-cell">
                    <button className="action-btn" title="Voir" onClick={() => onView(r)}><ViewIcon /></button>
                    <button className="action-btn" title="Modifier" onClick={() => onEdit(r)}><EditIcon /></button>
                    <Popconfirm title="Supprimer cette chambre ?" description={`${r.code}`} okText="Supprimer" cancelText="Annuler" okType="danger" onConfirm={() => onDelete(r)}>
                      <button className="action-btn danger" title="Supprimer"><DeleteIcon /></button>
                    </Popconfirm>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </TableCard>

      {/* Edit Drawer */}
      <Drawer title={editing ? 'Modifier chambre' : 'Nouvelle chambre'} placement="right" width={480} onClose={() => setOpen(false)} open={open} destroyOnClose extra={<div style={{ display: 'flex', gap: 8 }}><button className="btn-outline" onClick={() => setOpen(false)}>Annuler</button><button className="btn-primary" onClick={onSubmit}>{editing ? 'Enregistrer' : 'Créer'}</button></div>}>
        <Form form={form} layout="vertical">
          <Form.Item name="residence_id" label="Résidence" rules={[{ required: true }]}>
            <Select options={residences.map((r) => ({ value: r.id, label: r.nom }))} onChange={(v) => { form.setFieldsValue({ building_id: undefined }); setBuildingSearch(''); fetchBuildings(v); }} />
          </Form.Item>
          <Form.Item name="building_id" rules={[{ required: true }]} hidden><Input /></Form.Item>
          <Form.Item label="Bâtiment" required>
            <div style={{ display: 'flex', gap: 8 }}>
              <Input readOnly placeholder={!selectedResidenceFormId ? 'Sélectionnez d\'abord une résidence' : 'Choisir un bâtiment'} value={(() => { const b = (buildings || []).find((x) => String(x?.id) === String(selectedBuildingFormId)); return b?.nom || ''; })()} onClick={() => { if (!selectedResidenceFormId) return; setBuildingPickerOpen(true); }} style={{ cursor: 'pointer' }} />
              <button className="btn-outline" type="button" disabled={!selectedResidenceFormId} onClick={() => { if (!selectedResidenceFormId) return; setBuildingPickerOpen(true); }}>Choisir</button>
            </div>
          </Form.Item>
          <Form.Item name="code" label="Label" rules={[{ required: true }]}><Input placeholder="A-101" maxLength={32} /></Form.Item>
          <Form.Item name="capacity" label="Capacité" initialValue={1}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Drawer>

      {/* Bulk Drawer */}
      <Drawer title="Générer des chambres" placement="right" width={480} onClose={() => setBulkOpen(false)} open={bulkOpen} destroyOnClose extra={<div style={{ display: 'flex', gap: 8 }}><button className="btn-outline" onClick={() => setBulkOpen(false)}>Annuler</button><button className="btn-primary" disabled={bulkSubmitting} onClick={onBulkSubmit}>{bulkSubmitting ? 'En cours...' : 'Générer'}</button></div>}>
        <Form form={bulkForm} layout="vertical">
          <Form.Item name="residence_id" label="Résidence" rules={[{ required: true }]}>
            <Select options={residences.map((r) => ({ value: r.id, label: r.nom }))} onChange={(v) => { bulkForm.setFieldsValue({ building_id: undefined }); setBulkBuildingSearch(''); fetchBuildings(v); }} />
          </Form.Item>
          <Form.Item name="building_id" rules={[{ required: true }]} hidden><Input /></Form.Item>
          <Form.Item label="Bâtiment" required>
            <div style={{ display: 'flex', gap: 8 }}>
              <Input readOnly placeholder={!selectedResidenceBulkId ? 'Sélectionnez d\'abord une résidence' : 'Choisir un bâtiment'} value={(() => { const b = (buildings || []).find((x) => String(x?.id) === String(selectedBuildingBulkId)); return b?.nom || ''; })()} onClick={() => { if (!selectedResidenceBulkId) return; setBulkBuildingPickerOpen(true); }} style={{ cursor: 'pointer' }} />
              <button className="btn-outline" type="button" disabled={!selectedResidenceBulkId} onClick={() => { if (!selectedResidenceBulkId) return; setBulkBuildingPickerOpen(true); }}>Choisir</button>
            </div>
          </Form.Item>
          <p style={{ marginBottom: 12, fontSize: 12, color: '#888' }}>Plage automatique : 01..N (N = capacité chambres du bâtiment sélectionné).</p>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item name="separator" label="Séparateur" style={{ flex: 1 }}>
              <Select options={[{ value: '-', label: '-' }, { value: '', label: '(aucun)' }]} />
            </Form.Item>
            <Form.Item name="pad" label="Nb digits" style={{ flex: 1 }}>
              <InputNumber min={0} max={6} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <Form.Item name="capacity" label="Capacité" initialValue={1}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Drawer>

      {/* Building pickers (form) */}
      <Modal title="Choisir un bâtiment" open={buildingPickerOpen} onCancel={() => setBuildingPickerOpen(false)} onOk={() => setBuildingPickerOpen(false)} okText="OK">
        <Input placeholder="Rechercher un bâtiment" value={buildingSearch} onChange={(e) => setBuildingSearch(e.target.value)} style={{ marginBottom: 12 }} />
        <Table size="small" rowKey="id" dataSource={filteredBuildings} loading={buildingsLoading} pagination={{ hideOnSinglePage: true, pageSize: 8, size: 'small' }}
          rowSelection={{ type: 'radio', selectedRowKeys: selectedBuildingFormId ? [Number(selectedBuildingFormId)] : [], onChange: (keys) => { form.setFieldsValue({ building_id: keys?.[0] ?? null }); } }}
          columns={[{ title: 'Nom', dataIndex: 'nom' }, { title: 'Capacité (chambres)', dataIndex: 'capacite_chambres', width: 180 }]} />
      </Modal>

      {/* Building picker (bulk) */}
      <Modal title="Choisir un bâtiment" open={bulkBuildingPickerOpen} onCancel={() => setBulkBuildingPickerOpen(false)} onOk={() => setBulkBuildingPickerOpen(false)} okText="OK">
        <Input placeholder="Rechercher un bâtiment" value={bulkBuildingSearch} onChange={(e) => setBulkBuildingSearch(e.target.value)} style={{ marginBottom: 12 }} />
        <Table size="small" rowKey="id" dataSource={filteredBulkBuildings} loading={buildingsLoading} pagination={{ hideOnSinglePage: true, pageSize: 8, size: 'small' }}
          rowSelection={{ type: 'radio', selectedRowKeys: selectedBuildingBulkId ? [Number(selectedBuildingBulkId)] : [], onChange: (keys) => { bulkForm.setFieldsValue({ building_id: keys?.[0] ?? null }); } }}
          columns={[{ title: 'Nom', dataIndex: 'nom' }, { title: 'Capacité (chambres)', dataIndex: 'capacite_chambres', width: 180 }]} />
      </Modal>

      {/* View occupants modal */}
      <Modal title={viewRoom ? `Occupants — ${viewRoom.code}` : 'Occupants'} open={viewOpen} onCancel={() => setViewOpen(false)} footer={<button className="btn-outline" onClick={() => setViewOpen(false)}>Fermer</button>} width={700}>
        <div className="table-card"><div className="table-scroll">
          {viewLoading ? <div className="table-loading">Chargement…</div> : (
            <table><thead><tr><th>ID étudiant</th><th>Étudiant</th><th>Email</th><th>Début</th><th>Fin</th></tr></thead>
              <tbody>
                {pagedViewAssignments.map((a) => (
                  <tr key={a.id}>
                    <td>{a?.Student?.student_code || '-'}</td>
                    <td>{[a?.Student?.User?.prenom, a?.Student?.User?.nom].filter(Boolean).join(' ') || '-'}</td>
                    <td>{a?.Student?.User?.email || '-'}</td>
                    <td>{a?.start_date ? new Date(a.start_date).toLocaleString() : '-'}</td>
                    <td>{a?.end_date ? new Date(a.end_date).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div></div>
        {!viewLoading && (
        <div style={{ marginTop: 8 }}>
          <AntPagination current={viewPage} total={viewAssignments.length} pageSize={viewPageSize} onChange={setViewPage} />
        </div>
        )}
      </Modal>
    </div>
  );
}

